import { FileFolderNameExtensions } from "./FileFolderNameExtensions";
import { removeExtension } from "../components/helpers/stringHelper";
import { IFileToUpload } from "../interfaces/IFileToUpload";
import { IOfficeService } from "../interfaces/IOfficeService";
import { OfficeServiceEnum } from "../components/enums/OfficeService";
import { logger } from "../../../utilities/logger";
import { OutlookUtils } from "../../../utilities/OutlookUtils";
import { BatchRequestStep } from "@microsoft/microsoft-graph-client/lib/src/browser";
import { getAuthenticatedClient } from "./GraphClientService";
import authModule from "../../../utilities/AuthModule";
import { executeBatchWithRetry, GRAPH_MAILBOX_CONCURRENCY, IBatchStepResult, isRetryableBatchStatus } from "./GraphBatchService";
import { messageHeaderSelectFields } from "./graphMessageFields";
import { ISelectedMailboxTarget, selectedExchangeItemsStore } from "./selectedExchangeItemsStore";
import { discoverMailboxTarget, recordSharedMailboxContext } from "./sharedMailboxDiscovery";

interface SelectedMailIem {
  itemId: string;
  normalizedSubject: string;
  attachments?: Office.AttachmentDetails[];
  graphMailItemId?: string;
}

// Upper bound on waiting for an Office async callback. The Office host
// occasionally never invokes one; without a bound the file list would hang
// forever and the pending cache marker would outlive the load.
const officeCallbackTimeoutMs: number = 30000;

// The shared-mailbox probe rides the critical path of every multi-select
// load (concurrently with token acquisition), so it gets a tighter bound:
// a hung probe degrades to "me", the pre-existing behavior.
const mailboxProbeTimeoutMs: number = 10000;

// The hint capture rides every single-item load of a shared item, so it gets
// the same tight bound the other item-scoped shared-properties reads use.
const sharedHintTimeoutMs: number = 1500;

// Attachment metadata only — contentBytes must never ride along.
const attachmentSelectFields: string = "id,name,isInline,size,contentType";

export class OutlookService implements IOfficeService {
  serviceType: OfficeServiceEnum;
  constructor() {
    this.serviceType = OfficeServiceEnum.outlook;
  }

  static getItemRestId = (itemId: string) => {
    if (itemId) {
      try {
        const hostName: string = Office.context?.mailbox?.diagnostics?.hostName;
        if (hostName === "OutlookIOS" || hostName === "OutlookAndroid") {
          return itemId;
        }
        else {
          return Office.context?.mailbox?.convertToRestId(itemId, Office.MailboxEnums.RestVersion.v2_0);
        }
      } catch (error) {
        logger(error, "OutlookService.ts (getItemRestId)");
      }
    }
    return null;
  };

  static getItemEWSId = (itemId: string) => {
    if (itemId) {
      try {
        const hostName: string = Office.context?.mailbox?.diagnostics?.hostName;
        if (hostName === "OutlookIOS" || hostName === "OutlookAndroid") {
          return null;
        }
        else {
          return itemId;
        }
      } catch (error) {
        logger(error, "OutlookService.ts (getItemEWSId)");
      }
    }
    return null;
  };

  getRootItemName = async () => {
    // mailbox.item is undefined when multiple emails are selected; the file list
    // then has no single root item and renders its multi-file header instead.
    return Office.context?.mailbox?.item?.normalizedSubject;
  };

  // Promisifies an Office async callback with a sync-throw guard and a
  // timeout: Office callbacks can throw synchronously or never fire, and both
  // must degrade to null instead of hanging the caller.
  private static awaitOfficeCallback = <T>(invoke: (done: (value: T) => void) => void, timeoutMs: number = officeCallbackTimeoutMs): Promise<T | null> => {
    return new Promise<T | null>(resolve => {
      const timeoutHandle = setTimeout(() => resolve(null), timeoutMs);
      const done = (value: T) => {
        clearTimeout(timeoutHandle);
        resolve(value);
      };
      try {
        invoke(done);
      } catch (officeError) {
        clearTimeout(timeoutHandle);
        logger(officeError, "OutlookService.ts (awaitOfficeCallback)");
        resolve(null);
      }
    });
  };

  // Multi-select leaves mailbox.item null, so the shared-mailbox detection the
  // single-item flows do through item.getSharedPropertiesAsync needs a probed
  // item instead: load ONE selected item (per the multi-select contract the
  // whole selection lives in a single mailbox and folder), read its shared
  // properties when the API marks it shared — getSharedPropertiesAsync only
  // exists on items in shared folders/mailboxes — and unload it again.
  // loadItemByIdAsync requires Mailbox 1.15; its absence, or any probe
  // failure, degrades to "me": the pre-existing behavior.
  //
  // The probed identity (targetMailbox + targetRestUrl) is returned alongside
  // the Graph resource because the save payload needs it too: the persistor
  // runs long after this probe, when no item context exists to re-derive it.
  private static resolveMultiSelectMailboxResource = async (firstItemId: string): Promise<{ mailboxResource: string; mailboxTarget?: ISelectedMailboxTarget }> => {
    const ownMailbox = { mailboxResource: "me" };
    try {
      if (!(Office.context?.mailbox as any)?.loadItemByIdAsync) {
        return ownMailbox;
      }

      const loadedItem: any = await OutlookService.awaitOfficeCallback<any>(done => {
        (Office.context.mailbox as any).loadItemByIdAsync(firstItemId, (result: any) => {
          done(result?.status === Office.AsyncResultStatus.Succeeded ? result.value : null);
        });
      }, mailboxProbeTimeoutMs);

      if (!loadedItem) {
        return ownMailbox;
      }

      try {
        // OWA (observed on build 20260626009.06) omits getSharedPropertiesAsync
        // from loaded items even when they live in a shared mailbox, contrary
        // to the LoadedMessageRead docs — the discovery fallback covers that.
        if (!loadedItem.getSharedPropertiesAsync) {
          return ownMailbox;
        }
        const sharedProperties: Office.SharedProperties = await OutlookService.awaitOfficeCallback<Office.SharedProperties>(done => {
          loadedItem.getSharedPropertiesAsync((result: any) => {
            done(result?.status === Office.AsyncResultStatus.Succeeded ? result.value : null);
          });
        }, mailboxProbeTimeoutMs);

        if (!sharedProperties?.targetMailbox) {
          return ownMailbox;
        }
        return {
          mailboxResource: `users/${sharedProperties.targetMailbox}`,
          mailboxTarget: { targetMailbox: sharedProperties.targetMailbox, targetRestUrl: sharedProperties.targetRestUrl }
        };
      } finally {
        // Only one item may be loaded at a time; a leaked load would break the
        // next probe. unloadAsync's own completion is fire-and-forget.
        loadedItem.unloadAsync?.(() => { /* released */ });
      }
    } catch (error) {
      logger(error, "OutlookService.ts (resolveMultiSelectMailboxResource)");
      return ownMailbox;
    }
  };

  // The OPEN single item is the one place OWA reliably exposes
  // getSharedPropertiesAsync (it omits the method from items returned by
  // loadItemByIdAsync even when they live in a shared mailbox). Record every
  // shared context the user opens as a validation candidate for later
  // multi-selects; see sharedMailboxDiscovery for the guarantees.
  private static captureSharedMailboxHint = async (): Promise<void> => {
    try {
      const openItem: any = Office.context?.mailbox?.item;
      if (!openItem?.getSharedPropertiesAsync) {
        return;
      }
      const sharedProperties: Office.SharedProperties = await OutlookService.awaitOfficeCallback<Office.SharedProperties>(done => {
        openItem.getSharedPropertiesAsync((result: any) => {
          done(result?.status === Office.AsyncResultStatus.Succeeded ? result.value : null);
        });
      }, sharedHintTimeoutMs);

      if (sharedProperties?.targetMailbox) {
        recordSharedMailboxContext({ targetMailbox: sharedProperties.targetMailbox, targetRestUrl: sharedProperties.targetRestUrl });
      }
    } catch (error) {
      logger(error, "OutlookService.ts (captureSharedMailboxHint)");
    }
  };

  getItems = async (autoSelectAttachments?: boolean): Promise<IFileToUpload[]> => {
    const filesToUpload: IFileToUpload[] = [];
    const selectedMailItems: SelectedMailIem[] = [];

    // The cached headers only ever describe the current selection; stale
    // entries from a previous run must never leak into this one, and readers
    // that race this load await it instead of misreading the transient empty
    // cache. Generation guarding and the pending marker live in the store.
    const headersLoad = selectedExchangeItemsStore.beginLoad();

    try {
      if (Office.context?.mailbox?.item) {
        const { itemId, normalizedSubject, attachments } = Office.context?.mailbox?.item;
        if (!itemId) return [];
        selectedMailItems.push({ itemId, normalizedSubject, attachments });
        await OutlookService.captureSharedMailboxHint();
      }
      else if (Office.context?.mailbox?.getSelectedItemsAsync) {
        const selectedItemsResult: any = await OutlookService.awaitOfficeCallback<any>(done => {
          Office.context.mailbox.getSelectedItemsAsync(asyncResult => done(asyncResult));
        });

        try {
          if (!selectedItemsResult) {
            logger(new Error("getSelectedItemsAsync did not complete."), "OutlookService.ts (getSelectedItemsAsync)");
          }
          else if (selectedItemsResult.status === Office.AsyncResultStatus.Failed) {
            logger(new Error(selectedItemsResult.error.message), "OutlookService.ts (getSelectedItemsAsync)");
          }
          else if (Array.isArray(selectedItemsResult.value) && selectedItemsResult.value.length > 0) {
            const selectedItems: Office.SelectedItemDetails[] = selectedItemsResult.value;

            // The probe only costs wall clock the token fetch doesn't already
            // spend; a shared mailbox routes to users/{owner}, else "me".
            const [probeResult, graphAccessToken] = await Promise.all([
              OutlookService.resolveMultiSelectMailboxResource(selectedItems[0].itemId),
              authModule.getGraphAccessToken()
            ]);
            let { mailboxResource, mailboxTarget } = probeResult;

            const client = getAuthenticatedClient(graphAccessToken);

            // Convert once per message: the same REST id keys the batch steps, the
            // cached headers and the files (graphMailItemId), so per-file lookups
            // always match and the three uses can never silently diverge.
            const messageRestIds: string[] = selectedItems.map(selectedItem => OutlookService.getItemRestId(selectedItem.itemId));

            // Office probe blind (OWA hides shared properties on loaded items):
            // prove the recorded shared contexts against this selection.
            if (!mailboxTarget) {
              const discoveredTarget = await discoverMailboxTarget(client, messageRestIds[0]);
              if (discoveredTarget) {
                mailboxResource = `users/${discoveredTarget.targetMailbox}`;
                mailboxTarget = discoveredTarget;
              }
            }

            // One step per message — headers plus attachment metadata in a single
            // GET via $expand (documented for messages; the nested $select keeps
            // contentBytes out). Steps run in strictly sequential batches with
            // per-step retry: Graph evaluates every batch step against the Outlook
            // concurrency limit (4 per mailbox), so parallel batch posts get their
            // steps throttled and the SDK does not retry throttled steps in a batch.
            // Use synthetic batch ids: Graph treats the batch request id as
            // case-insensitive and rejects the whole batch (400) on duplicates;
            // Exchange message ids are case-sensitive base64 tokens that can
            // collide case-insensitively.
            const messageRequestQuery: string = `$select=${messageHeaderSelectFields}&$expand=attachments($select=${attachmentSelectFields})`;
            const batchSteps: BatchRequestStep[] = selectedItems.map((_selectedItem, index) => {
              return {
                id: `message-${index}`,
                request: new Request(`/${mailboxResource}/messages/${messageRestIds[index]}?${messageRequestQuery}`, {
                  method: "GET"
                })
              };
            });

            let stepResults = new Map<string, IBatchStepResult>();
            try {
              ({ stepResults } = await executeBatchWithRetry(client, batchSteps));
            } catch (batchError) {
              // Total batch failure: every message falls back to the individual
              // GETs below. The save is never blocked.
              logger(batchError, "OutlookService.ts (getItems - $batch)");
            }

            // Residual (post-retry) failures fall back to individual GETs, which
            // the Graph SDK middleware retries on its own — but only for failures
            // a retry can plausibly cure (throttling/transients, or a missing
            // result from a failed batch). A step that failed 403/404 would fail
            // identically outside the batch. Fallbacks run in mailbox-concurrency-
            // sized waves so a total batch outage cannot serialize one full round
            // trip per selected message.
            const fallbackIndexes: number[] = [];
            for (let index = 0; index < selectedItems.length; index++) {
              const messageResult = stepResults.get(`message-${index}`);
              if (!messageResult?.ok && (!messageResult || messageResult.status === 0 || isRetryableBatchStatus(messageResult.status))) {
                fallbackIndexes.push(index);
              }
            }

            const fallbackResults = new Map<number, any>();
            for (let i = 0; i < fallbackIndexes.length; i += GRAPH_MAILBOX_CONCURRENCY) {
              await Promise.all(fallbackIndexes.slice(i, i + GRAPH_MAILBOX_CONCURRENCY).map(async index => {
                try {
                  const fallbackMessage = await client.api(`/${mailboxResource}/messages/${messageRestIds[index]}`)
                    .select(messageHeaderSelectFields)
                    .expand(`attachments($select=${attachmentSelectFields})`)
                    .get();
                  fallbackResults.set(index, fallbackMessage);
                } catch (messageError) {
                  logger(messageError, "OutlookService.ts (getItems - message fallback)");
                }
              }));
            }

            const selectedMessageHeaders: any[] = [];
            for (let index = 0; index < selectedItems.length; index++) {
              const selectedItem: Office.SelectedItemDetails = selectedItems[index];
              const messageRestId: string = messageRestIds[index];

              const messageResult = stepResults.get(`message-${index}`);
              const message: any = (messageResult?.ok ? messageResult.body : null) ?? fallbackResults.get(index) ?? null;

              let attachments: Office.AttachmentDetails[];
              if (Array.isArray(message?.attachments) && message.attachments.length > 0) {
                attachments = message.attachments
                  .filter(attachment => attachment?.id && !attachment.isInline && attachment.size > 0);
              }

              // Key the cached headers by the same REST id stamped on the files
              // (graphMailItemId) so per-file lookups always match, regardless of
              // how Graph encodes the id it returns. The expanded attachments are
              // stripped — the cache carries mapping headers only. A message that
              // could not be resolved still gets a stub entry: the cached array
              // must hold one entry per selected message, so the metadata form can
              // never mistake a partial set for a smaller selection (a reduction
              // to one entry would direct-map that email's values onto every
              // file). The stub resolves every mapped property to undefined, so
              // only that message's fields degrade to unmapped values.
              if (message) {
                const { attachments: _expandedAttachments, ...messageHeaders } = message;
                selectedMessageHeaders.push({ ...messageHeaders, id: messageRestId });
              } else {
                selectedMessageHeaders.push({ id: messageRestId });
              }

              selectedMailItems.push({ itemId: selectedItem.itemId, normalizedSubject: selectedItem.subject, attachments, graphMailItemId: messageRestId });
            }

            // With no resolved target, the /me header batch doubles as a free
            // ownership proof: message ids resolve only inside their own
            // mailbox, so EVERY step 404ing means the selection provably lives
            // in a mailbox we could not identify — a save would fail for every
            // file, and the save UI blocks on this flag instead. Any non-404
            // outcome (success, throttle, transient) leaves the flag off: the
            // proof must be unanimous, never inferred from partial failures.
            const unresolvedSharedSelection: boolean = !mailboxTarget
              && mailboxResource === "me"
              && selectedItems.length > 0
              && selectedItems.every((_selectedItem, index) =>
                stepResults.get(`message-${index}`)?.status === 404 && !fallbackResults.has(index));

            // The store discards this publish when a newer load/publish/clear
            // superseded it: the selection may have changed under this run, and
            // writing possibly out-of-date headers risks stamping another
            // selection's values onto the files — an empty cache degrades safely
            // to unmapped fields, wrong headers persist wrong metadata. The
            // probed mailbox target rides the same publish so a save can never
            // pair one selection's files with another selection's mailbox.
            headersLoad.publish(selectedMessageHeaders, mailboxTarget, unresolvedSharedSelection);
          }
        } catch (error) {
          // A throw anywhere above must degrade to an empty / partial file list
          // (unmapped fields at worst), never block the save or strand the UI.
          logger(error, "OutlookService.ts (getItems - multi-select fetch)");
        }
      }
    } finally {
      headersLoad.finish();
    }

    for (let index = 0; index < selectedMailItems.length; index++) {
      try {
        const selectedMailItem = selectedMailItems[index];
        const { itemId, normalizedSubject, attachments, graphMailItemId } = selectedMailItem;

        // graphMailItemId already holds the converted id in the multi-select
        // flow; only the single-item flow still needs the conversion here.
        const messageRestId: string = graphMailItemId ?? OutlookService.getItemRestId(itemId);

        const itemAttachments = Array.isArray(attachments) && attachments.length > 0
          ? attachments
            .filter(attachment => attachment?.id && !attachment.isInline && attachment.size > 0)
            .map(attachment => {
              return {
                id: OutlookService.getItemRestId(attachment.id),
                ewsId: OutlookService.getItemEWSId(attachment.id),
                parentId: messageRestId,
                name: removeExtension(FileFolderNameExtensions.EnsureValidName(attachment.name)),
                extension: OutlookUtils.getExtension(attachment),
                isSelected: autoSelectAttachments === true,
                isAttachment: true,
                isDuplicate: false,
                contentType: attachment.contentType,
                // Attachments inherit the parent email's header values in the
                // per-file mapping, matching the content-manager copy-emails flow.
                graphMailItemId
              };
            })
            .filter(attachment => attachment?.id)
          : [];

        const attachmentsWithEmptyExtension = itemAttachments.filter(a => a.ewsId && !a.extension && a.contentType && a.contentType !== "application/octet-stream");
        if (attachmentsWithEmptyExtension?.length > 0) {
          const ewsUrl: string = OutlookUtils.getEWSURL();
          const ewsToken: string = await OutlookUtils.getEWSToken();

          if (ewsUrl && ewsToken) {
            const extensionData = await OutlookUtils.requestExtensions(ewsUrl, ewsToken, attachmentsWithEmptyExtension.map(a => a.ewsId));
            if (Array.isArray(extensionData)) {
              for (let i = 0; i < extensionData.length; i++) {
                const extData = extensionData[i];
                const attachment = itemAttachments.find(a => a.ewsId === extData.id);
                if (attachment) {
                  attachment.extension = extData.extension;
                }
              }
            }
          }
        }

        const mainMailItem = {
          id: messageRestId,
          ewsId: OutlookService.getItemEWSId(itemId),
          name: FileFolderNameExtensions.EnsureValidName(normalizedSubject),
          extension: "eml",
          isSelected: autoSelectAttachments !== true || (autoSelectAttachments === true && !itemAttachments?.length),
          isAttachment: false,
          isDuplicate: false,
          isMainMailItem: true,
          graphMailItemId
        };

        filesToUpload.push(...[mainMailItem, ...itemAttachments]);
      } catch (error) {
        console.error(error);
      }
    }

    return filesToUpload;
  };
}
